import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';
import type { PromptTemplateCategory, PromptTemplateCategorySaveForm } from '../../api';

const PRESET_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
  '#14B8A6',
  '#A855F7',
];

interface CategoryFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: PromptTemplateCategorySaveForm) => Promise<void>;
  category?: PromptTemplateCategory | null;
}

export default function CategoryFormModal({
  open,
  onClose,
  onSubmit,
  category,
}: CategoryFormModalProps) {
  const [title, setTitle] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(category?.id);

  useEffect(() => {
    if (open) {
      setTitle(category?.title ?? '');
      setColor(category?.color ?? '#3B82F6');
      setDescription(category?.description ?? '');
      setError(null);
    }
  }, [open, category]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError('Введите название категории');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSubmit({ title: title.trim(), color, description: description.trim() || undefined });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  }, [title, color, description, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [loading, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? 'Редактировать категорию' : 'Создать категорию'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          label="Название"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          autoFocus
        />
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 14, color: '#666' }}>
            Цвет
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: color === c ? '3px solid #333' : '1px solid #ddd',
                  background: c,
                  cursor: 'pointer',
                }}
              />
            ))}
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }}
            />
          </div>
        </div>
        <TextField
          label="Описание (необязательно)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
        {error && <div style={{ color: '#d32f2f', fontSize: 14 }}>{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Отмена
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : isEdit ? 'Сохранить' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
